(() => {
  const roots = document.querySelectorAll('[data-article-floating-tee-deal]');
  if (!roots.length) return;

  roots.forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const canvas = root.querySelector('[data-floating-canvas]');
    const closeButton = root.querySelector('[data-floating-close]');
    const shuffleButton = root.querySelector('[data-floating-shuffle]');
    const copyButton = root.querySelector('[data-floating-copy]');
    const productLinks = [...root.querySelectorAll('[data-floating-product]')];
    const titleLink = root.querySelector('[data-floating-title]');
    const proofLine = root.querySelector('[data-floating-proof]');
    const fallbackImage = root.querySelector('[data-floating-image]');

    const articleId = root.dataset.articleId || 'unknown';
    const articleHandle = root.dataset.articleHandle || '';
    const articleTitle = root.dataset.articleTitle || document.title;
    const discountCode = root.dataset.discountCode || 'WELCOME10';
    const accentColor = root.dataset.accentColor || '#ef272d';
    const delayMs = Math.max(0, Number(root.dataset.delaySeconds || 5) * 1000);
    const randomLimit = Math.min(50, Math.max(1, Number(root.dataset.randomLimit || 50)));
    const socialProofUrl = root.dataset.socialProofUrl || '';
    const dismissKey = `wahup_floating_tee_deal_dismissed_${articleId}`;
    const mobileChatQuery = window.matchMedia('(max-width: 749px)');
    const chatSelectors = [
      '#ShopifyChat',
      '[id^="ShopifyChat"]',
      '[id*="shopify-chat"]',
      '[class*="shopify-chat"]',
      '[class*="ShopifyChat"]',
      'inbox-online-store-chat',
      'iframe[src*="shopifychat"]',
      'iframe[src*="shopify.com/inbox"]',
      'iframe[src*="inbox.shopify"]',
      'iframe[title*="chat" i]',
      'iframe[aria-label*="chat" i]',
      '[aria-label*="chat" i][role="button"]',
      '[data-testid*="chat" i]'
    ];

    let productId = root.dataset.productId || '';
    let productHandle = root.dataset.productHandle || '';
    let productTitle = root.dataset.productTitle || 'Fresh tee deal';
    let productUrl = root.dataset.productUrl || '/collections/new-arrivals';
    let productImage = root.dataset.productImage || '';
    let productImages = [];
    let products = [];
    let renderer = null;
    let scene = null;
    let camera = null;
    let card = null;
    let cardMaterial = null;
    let rafId = 0;
    let resizeObserver = null;
    let threeStarted = false;
    let pointerX = 0;
    let pointerY = 0;

    root.style.setProperty('--floating-accent', accentColor);

    const parseProductImages = () => {
      try {
        const images = JSON.parse(root.dataset.productImages || '[]');
        return [...new Set(images.filter(Boolean))].slice(0, 4);
      } catch (error) {
        return [];
      }
    };

    productImages = parseProductImages();

    const track = (event, details = {}) => {
      const eventName = `floating_tee_deal_${event}`;
      const parameters = {
        article_id: String(articleId),
        article_handle: articleHandle,
        article_title: articleTitle,
        product_id: String(productId),
        product_handle: productHandle,
        product_title: productTitle,
        product_url: productUrl,
        offer_type: 'floating_tee_deal',
        discount_code: discountCode,
        shipping_message: 'Ships in 2-5 days',
        page_location: window.location.href,
        device_type: mobileChatQuery.matches ? 'mobile' : 'desktop',
        ...details
      };
      root.dataset.lastTrackedEvent = eventName;
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, parameters);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...parameters });
    };

    const hideMobileChat = () => {
      if (!mobileChatQuery.matches) return;
      chatSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => {
          element.hidden = true;
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('opacity', '0', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        });
      });
    };

    const isDismissed = () => {
      try {
        const dismissedAt = Number(localStorage.getItem(dismissKey) || 0);
        return dismissedAt > Date.now() - 24 * 60 * 60 * 1000;
      } catch (error) {
        return false;
      }
    };

    const saveDismissed = () => {
      try {
        localStorage.setItem(dismissKey, String(Date.now()));
      } catch (error) {
        // The close button should still work without storage.
      }
    };

    const pickRandomItem = (items, currentHandle = '') => {
      const pool = items.filter((item) => item.handle !== currentHandle);
      const candidates = pool.length ? pool : items;
      if (!candidates.length) return null;
      if (window.crypto?.getRandomValues) {
        const values = new Uint32Array(1);
        window.crypto.getRandomValues(values);
        return candidates[values[0] % candidates.length];
      }
      return candidates[Math.floor(Math.random() * candidates.length)];
    };

    const hasAvailableVariant = (product) => (
      product?.variants || []
    ).some((variant) => variant.available !== false);

    const updateLinks = () => {
      productLinks.forEach((link) => {
        link.href = productUrl;
      });
    };

    const updateTexture = async () => {
      if (!cardMaterial || !productImage) return;
      try {
        const THREE = await import('https://cdn.jsdelivr.net/npm/three@0.160.1/build/three.module.js');
        const texture = await new THREE.TextureLoader().loadAsync(productImage);
        texture.colorSpace = THREE.SRGBColorSpace;
        cardMaterial.map = texture;
        cardMaterial.needsUpdate = true;
      } catch (error) {
        console.error('Floating Tee Deal could not update product texture.', error);
      }
    };

    const setProduct = async (product, animate = false) => {
      const images = (product?.images || [])
        .map((image) => image.src)
        .filter(Boolean);
      if (!product || !images.length) return false;

      productId = String(product.id || productId);
      productHandle = product.handle || productHandle;
      productTitle = product.title || productTitle;
      productUrl = product.handle ? `/products/${product.handle}` : productUrl;
      productImages = [...new Set(images)].slice(0, 4);
      productImage = productImages[0] || productImage;

      root.dataset.productId = productId;
      root.dataset.productHandle = productHandle;
      root.dataset.productTitle = productTitle;
      root.dataset.productUrl = productUrl;
      root.dataset.productImage = productImage;
      root.dataset.productImages = JSON.stringify(productImages);

      if (titleLink) {
        titleLink.textContent = productTitle;
        titleLink.href = productUrl;
      }
      updateLinks();
      if (fallbackImage) fallbackImage.src = productImage;

      if (animate) {
        root.classList.remove('is-flipping');
        root.offsetHeight;
        root.classList.add('is-flipping');
        window.setTimeout(() => root.classList.remove('is-flipping'), 430);
      }
      await updateTexture();
      return true;
    };

    const setSocialProofEvent = async (event) => {
      if (!event || !event.productUrl || !event.headline) return false;
      if (proofLine) {
        proofLine.textContent = event.headline;
        proofLine.hidden = false;
      }
      productId = '';
      productHandle = event.productUrl.split('/products/')[1] || productHandle;
      productTitle = event.productTitle || productTitle;
      productUrl = event.productUrl;
      productImage = event.productImage || productImage;
      root.dataset.productHandle = productHandle;
      root.dataset.productTitle = productTitle;
      root.dataset.productUrl = productUrl;
      root.dataset.productImage = productImage;
      if (titleLink) {
        titleLink.textContent = event.subtext || 'Recently bought · Ships in 2-5 days';
        titleLink.href = productUrl;
      }
      updateLinks();
      if (fallbackImage && productImage) fallbackImage.src = productImage;
      await updateTexture();
      track('real_order_loaded', {
        social_proof_location: event.location || '',
        social_proof_minutes_ago: event.minutesAgo || ''
      });
      return true;
    };

    const hydrateRealSocialProof = async () => {
      if (!socialProofUrl) return false;
      try {
        const url = new URL(socialProofUrl);
        url.searchParams.set('shop', 'wahup189.myshopify.com');
        url.searchParams.set('hours', '72');
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return false;
        const data = await response.json();
        const events = Array.isArray(data.events) ? data.events : [];
        const event = pickRandomItem(events);
        if (!event) return false;
        return setSocialProofEvent(event);
      } catch (error) {
        console.error('Floating Tee Deal could not load real social proof.', error);
        return false;
      }
    };

    const loadProducts = async () => {
      if (products.length) return products;
      try {
        const response = await fetch(`/products.json?limit=${randomLimit}&order=created_at%20desc`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) return [];
        const data = await response.json();
        products = (data.products || [])
          .filter((product) => hasAvailableVariant(product))
          .filter((product) => (product.images || []).some((image) => image.src));
        return products;
      } catch (error) {
        console.error('Floating Tee Deal could not load newest products.', error);
        return [];
      }
    };

    const hydrateRandomProduct = async () => {
      const newestProducts = await loadProducts();
      const product = pickRandomItem(newestProducts, productHandle);
      if (!product) return false;
      return setProduct(product);
    };

    const resizeThree = () => {
      if (!renderer || !camera || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const startThree = async () => {
      if (threeStarted || !canvas || !productImage) return;
      threeStarted = true;

      try {
        const three = await import('https://cdn.jsdelivr.net/npm/three@0.160.1/build/three.module.js');
        const THREE = three;
        renderer = new THREE.WebGLRenderer({
          canvas,
          antialias: true,
          alpha: true
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
        camera.position.set(0, 0, 4.6);

        const ambient = new THREE.AmbientLight(0xffffff, 1.45);
        const key = new THREE.DirectionalLight(0xffffff, 2.1);
        key.position.set(2, 3, 4);
        scene.add(ambient, key);

        const group = new THREE.Group();
        const texture = await new THREE.TextureLoader().loadAsync(productImage);
        texture.colorSpace = THREE.SRGBColorSpace;
        cardMaterial = new THREE.MeshBasicMaterial({ map: texture });
        card = new THREE.Mesh(new THREE.PlaneGeometry(2.25, 2.25), cardMaterial);

        const backing = new THREE.Mesh(
          new THREE.BoxGeometry(2.32, 2.32, 0.08),
          new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.36,
            metalness: 0.04
          })
        );
        backing.position.z = -0.06;
        group.add(backing, card);

        const particleMaterial = new THREE.PointsMaterial({
          color: accentColor,
          size: 0.035,
          transparent: true,
          opacity: 0.55
        });
        const particlePositions = [];
        for (let index = 0; index < 46; index += 1) {
          particlePositions.push(
            (Math.random() - 0.5) * 3.2,
            (Math.random() - 0.5) * 3.2,
            -0.35 + Math.random() * 0.55
          );
        }
        const particleGeometry = new THREE.BufferGeometry();
        particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(particlePositions, 3));
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        group.add(particles);

        scene.add(group);
        resizeThree();
        root.classList.add('is-three-ready');

        resizeObserver = new ResizeObserver(resizeThree);
        resizeObserver.observe(canvas);

        const animate = () => {
          if (!renderer || !scene || !camera) return;
          const time = Date.now() / 1000;
          group.rotation.x = -0.08 + pointerY * 0.12 + Math.sin(time * 1.4) * 0.025;
          group.rotation.y = pointerX * 0.16 + Math.sin(time * 0.95) * 0.06;
          group.position.y = Math.sin(time * 1.6) * 0.035;
          particles.rotation.z += 0.0025;
          renderer.render(scene, camera);
          rafId = requestAnimationFrame(animate);
        };

        animate();
      } catch (error) {
        threeStarted = false;
        console.error('Floating Tee Deal could not load Three.js.', error);
      }
    };

    const showDeal = () => {
      if (isDismissed()) return;
      root.hidden = false;
      root.classList.add('is-visible');
      startThree();
      hideMobileChat();
      track('view');
      hydrateRealSocialProof().then((loadedRealOrder) => {
        if (loadedRealOrder) return true;
        return hydrateRandomProduct();
      }).then((updated) => {
        if (updated && !threeStarted) startThree();
      });
    };

    const closeDeal = () => {
      root.classList.remove('is-visible');
      window.setTimeout(() => {
        root.hidden = true;
      }, 220);
      saveDismissed();
      track('close');
    };

    const shuffleProduct = async () => {
      shuffleButton.disabled = true;
      const newestProducts = await loadProducts();
      const product = pickRandomItem(newestProducts, productHandle);
      if (product) {
        await setProduct(product, true);
        track('shuffle');
      }
      shuffleButton.disabled = false;
    };

    const copyDiscountCode = async () => {
      try {
        await navigator.clipboard.writeText(discountCode);
        copyButton.textContent = 'COPIED';
        window.setTimeout(() => {
          copyButton.textContent = discountCode;
        }, 1300);
        track('copy_code');
      } catch (error) {
        track('copy_code_failed');
      }
    };

    const handlePointerMove = (event) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
      pointerY = -((event.clientY - rect.top) / rect.height - 0.5) * 2;
    };

    hideMobileChat();
    new MutationObserver(hideMobileChat).observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'hidden', 'src', 'title', 'aria-label']
    });
    window.addEventListener('load', hideMobileChat);
    mobileChatQuery.addEventListener('change', hideMobileChat);
    window.setTimeout(showDeal, delayMs);
    closeButton?.addEventListener('click', closeDeal);
    shuffleButton?.addEventListener('click', shuffleProduct);
    copyButton?.addEventListener('click', copyDiscountCode);
    root.addEventListener('pointermove', handlePointerMove);
    root.addEventListener('pointerleave', () => {
      pointerX = 0;
      pointerY = 0;
    });
    productLinks.forEach((link) => {
      link.addEventListener('click', () => track('product_click'));
    });
  });
})();
